import {
  Component,
  EventEmitter,
  Prop,
  State,
  h,
  Event,
  Element,
  ComponentInterface,
  Listen,
  Method,
  Host
} from '@stencil/core';
import { ChipSize, ChipVariant } from './jui-chip.model';
import { IconSize, IconVariant } from '../jui-icon/jui-icon.model';

@Component({
  tag: 'jui-chip',
  styleUrl: 'jui-chip.scss',
  shadow: true
})
export class JuiChip implements ComponentInterface {
  private static REMOVE_DURATION = 150;
  private removeTimeout: NodeJS.Timeout | null = null;

  @Prop() size: ChipSize = ChipSize.sm;

  @Prop() color: ChipVariant = ChipVariant.default;

  @Prop() pill = false;

  @Prop() selectable = false;

  @Prop({ mutable: true }) value = '';

  @Prop() disable = false;

  @Prop() removable = false;

  @Prop() icon = true;

  @Prop({ mutable: true }) isSelected = false;

  @Prop() isRadio = false;

  /**
   * Optional data-qa string
   */
  @Prop() dataqastring: string = null;

  @State() selected = false;

  @State() showSelectedIcon = false;

  @State() removed = false;

  @State() removing = false;

  /**
   * If this chip is not part of a set
   */
  @Prop() single = true;

  @State() selectedValue = null;

  /**
   * The tabindex of the chip
   * @internal
   */
  @State() chipTabindex = this.isRadio ? -1 : 0;

  @Event() public juiChipChecked: EventEmitter<{ value: string; checked: boolean }>;

  @Event() public juiChecked: EventEmitter<{ value: string; checked: boolean }>;

  @Event() public juiChipRemoved: EventEmitter<{ value: string; checked: boolean }>;

  @Listen('keydown')
  keyDownHandler(evt: KeyboardEvent): void {
    if (evt.key === 'Enter' || evt.key === ' ') {
      evt.preventDefault();
      this.handleChipClick();
    }
  }

  @Element() el: HTMLElement;

  @Method()
  async setChipTabIndex(value: number) {
    this.chipTabindex = value;
  }

  @Method()
  async setFocused() {
    (this.el.shadowRoot.querySelector('.chip') as HTMLElement).focus();
  }

  handleChipClick(): void {
    if (!this.selectable || this.disable || this.removing) {
      return;
    }

    // choice type chipset
    if (this.isRadio) {
      !this.isSelected ? (this.isSelected = true) : true;
      !this.selected ? (this.selected = true) : true;
    } else {
      this.isSelected = !this.isSelected;
      this.selected = !this.selected;
    }

    this.showSelectedIcon = this.icon && this.selected;
    if (this.single) {
      this.juiChecked.emit({ value: this.selectedValue, checked: this.selected });
    } else {
      this.juiChipChecked.emit({ value: this.selectedValue, checked: this.selected });
    }
  }

  handleRemoveKeydown(evt: KeyboardEvent): void {
    if (evt.key === 'Enter' || evt.key === ' ') {
      evt.preventDefault();
      this.handleRemoveClick();
    }
  }

  handleRemoveClick(): void {
    if (this.disable) {
      return;
    }

    // a11y - respect users who cant view with motion
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.juiChipRemoved.emit({ value: this.selectedValue, checked: false });
      this.removed = true;
      return;
    }

    this.removing = true;

    if (this.removeTimeout !== null) {
      return;
    }

    this.removeTimeout = setTimeout(() => {
      this.juiChipRemoved.emit({ value: this.selectedValue, checked: false });
      this.removed = true;
    }, JuiChip.REMOVE_DURATION);
  }

  getIconSize(): IconSize {
    if (this.size === ChipSize.sm) {
      return IconSize.xxs;
    }
    if (this.size === ChipSize.md) {
      return IconSize.xs;
    }
    if (this.size === ChipSize.lg) {
      return IconSize.xs;
    }
  }

  getIconCursor(): string {
    return this.disable ? 'not-allowed' : null;
  }

  componentWillLoad(): void {
    if (this.value === '') {
      this.selectedValue = this.el.innerHTML.trim();
      this.value = this.selectedValue;
    } else {
      this.selectedValue = this.value;
    }
    this.selected = this.isSelected;
  }

  componentWillUpdate(): Promise<void> | void {
    this.selected = this.isSelected;
    this.showSelectedIcon = this.icon && this.selected;
  }

  componentDidLoad() {
    this.showSelectedIcon = this.icon && this.selected;
  }

  render() {
    if (this.removed) {
      return null;
    }

    return (
      <Host data-qa={this.dataqastring}>
        <span
          data-testid="chip-test"
          data-qa={this.dataqastring}
          tabindex={this.chipTabindex}
          class={{
            chip: true,
            [`chip--${this.size}`]: true,
            'chip--selectable': this.selectable,
            'chip--selected': this.selected,
            'chip--disabled': this.disable,
            'chip--removing': this.removing,
            'chip--pill': this.pill,
            [`chip--${this.color}`]: true
          }}
          onClick={this.handleChipClick.bind(this)}
        >
          {this.showSelectedIcon ? (
            <span data-id="selected" class="chip__icon chip__icon--appear">
              <jui-icon isButton={false} color={IconVariant.current} size={this.getIconSize()} name="done"></jui-icon>
            </span>
          ) : null}
          <slot></slot>
          {this.removable ? (
            <span data-id="remove" class="chip__icon">
              <jui-icon
                data-testid="remove"
                color={IconVariant.current}
                size={this.getIconSize()}
                cursor={this.getIconCursor()}
                name="close"
                onKeyDown={this.handleRemoveKeydown.bind(this)}
                onClick={this.handleRemoveClick.bind(this)}
              ></jui-icon>
            </span>
          ) : null}
        </span>
      </Host>
    );
  }
}
